import { CuriosityNamespaceType, CuriosityValue } from '@procureai/curiosity-ui-mui';
import { Permission } from '@procureai/permissions';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { NULLISH_VALUE } from '~/common/constants';
import { I18nNamespace } from '~/common/types';
import {
  cellFactory,
  CurrencyCell,
  DataGridConfig,
  EmptyFallbackCell,
  GridAlignmentType,
  gridColDefFactory,
  SupplierLinkCell,
  UseDataGridConfig,
  useDataGridMasterDetailConfig,
} from '~/components/DataGridCustom';
import { curiosityValueGetter } from '~/components/DataGridWidget/DataGridWidget.util';

import {
  PurchaseRequisitionDetailsDataGridField,
  PurchaseRequisitionDetailsDataGridRow,
} from './PurchaseRequisitionDetails.types';

export const usePurchaseRequisitionDetailsDataGridConfig: UseDataGridConfig<
  PurchaseRequisitionDetailsDataGridRow
> = () => {
  const { t } = useTranslation();
  const { t: tColumn } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'PurchaseRequisitionDetailsWidget.table' });

  const { defaultToggleColumnDefinition, ...masterDetailProps } =
    useDataGridMasterDetailConfig<PurchaseRequisitionDetailsDataGridRow>();

  return useMemo(() => {
    const createColumn = gridColDefFactory({ tColumn, t });
    return {
      sortModel: [],
      columnVisibilityModel: {},
      columns: [
        // required to include toggle column definition to preserve
        // column order on remote state reset invocation
        defaultToggleColumnDefinition,
        createColumn({
          field: PurchaseRequisitionDetailsDataGridField.POSITION,
          minWidth: 60,
          hideable: false,
          sortable: false,
        }),
        createColumn({
          field: PurchaseRequisitionDetailsDataGridField.TITLE,
          renderCell: cellFactory(EmptyFallbackCell),
          minWidth: 50,
          flex: 1,
          sortable: false,
        }),
        createColumn({
          field: PurchaseRequisitionDetailsDataGridField.SUPPLIER,
          renderCell: cellFactory(SupplierLinkCell, ({ value }) => ({
            value: {
              id: value,
              label: curiosityValueGetter(CuriosityNamespaceType.SUPPLIER, value),
            },
            needs: { permission: Permission.BaseSupplierList },
          })),
          minWidth: 200,
          sortable: false,
        }),
        createColumn({
          field: PurchaseRequisitionDetailsDataGridField.QUANTITY,
          renderCell: cellFactory(EmptyFallbackCell),
          minWidth: 150,
          align: GridAlignmentType.RIGHT,
          sortable: false,
        }),
        createColumn({
          field: PurchaseRequisitionDetailsDataGridField.UNIT,
          renderCell: cellFactory(CuriosityValue, ({ value }) => ({
            ns: CuriosityNamespaceType.UNIT,
            i18nKey: value,
            fallback: NULLISH_VALUE,
          })),
          minWidth: 150,
          align: GridAlignmentType.RIGHT,
          sortable: false,
        }),
        createColumn({
          field: PurchaseRequisitionDetailsDataGridField.PRICE_PER_UNIT,
          renderCell: cellFactory(CurrencyCell, ({ row }) => ({ currency: row.currency })),
          minWidth: 200,
          align: GridAlignmentType.RIGHT,
          sortable: false,
        }),
      ],
      ...masterDetailProps,
    } as DataGridConfig<PurchaseRequisitionDetailsDataGridRow>;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tColumn, t, defaultToggleColumnDefinition]);
};
